import React from 'react';
import _ from 'underscore';
import { MergeNodesIntoTree } from '@bingads-webui-react/tree-view';
import { Breadcrumbs } from '@bingads-webui-react/breadcrumbs';
import { SearchBox } from '@bingads-webui-react/search-box';
import { Checkbox } from '@bingads-webui-react/checkbox';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { CustomerHierarchyTreeView } from '@bingads-webui-react/customer-hierarchy-tree-view';
import { CustomerHierarchyClassName, DefaultUpdateEntityUrl, InActiveAccountLifeCycleStatus, AccountSelectorInactiveAccountsKey, ShowInactiveEntitiesKey } from './constants';
import { CustomerAccountNodeRenderer, SimpleNodeRenderer, DefaultNodeRenderer } from './customer-account-node-renderer';
import { AccountSelectorPropTypes } from './shapes';
import { submitEvent } from './event-wrappers';

const allAccountsTitleId = 'AllAccountsTitle';

// For complex state object, avoid PureComponent as it does shallow comparison of prev and new state
export class CustomerAccountSelector extends React.Component {
  constructor(props) {
    super(props);
    this.perfMarker = props.perfMarker;
    if (this.perfMarker) {
      this.perfMarkerSelect = this.perfMarker.createChild('Account_Dropdown_Select');
      this.perfMarkerSelect.willInit();
    }

    this.state = {
      nodes: [],
      showInactiveEntities: true,
      parentCustomerEntities: [],
      searchValue: '',
      loadingHierarchyData: false,
    };
    this.enableSearchContext = false;
    this.config = {
      autoHeight: props.autoHeight,
      batchSize: props.batchSize,
      enableOnScrollLoad: props.enableOnScrollLoad,
      returnOnlyDirectlyLinkedEntities: props.returnOnlyDirectlyLinkedEntities,
    };
  }

  componentDidMount = () => {
    this.fetchAncestorHierarchyPath();
  }

  componentDidUpdate = (prevProps) => {
    // Right now only rootCustomerId can change between renders
    // So only checking for rootCustomerId
    if (prevProps.rootCustomerId !== this.props.rootCustomerId) {
      this.fetchAncestorHierarchyPath();
    }
  }

  onSearch = () => {
    this.enableSearchContext = !_.isEmpty(this.state.searchValue);
    this.fetchCustomerHierarchy({
      cid: this.props.rootCustomerId,
      isFromSearch: true,
    });
  }

  onQueryChange = (searchValue) => {
    this.setState({ searchValue });
  }

  onChange = (nodes) => {
    this.setState({ nodes });
  }

  getNodeSpecificClassNames = node =>
    (node && node.isCurrent ? ['current-node'] : []);

  getShowInactveEntitesUserPreference = () => {
    this.props.ccCustomerService.getUserPreferences({
      uid: this.props.userId,
      id: AccountSelectorInactiveAccountsKey,
    }).then((data) => {
      if (data && data.length > 0) {
        const preference = data[0].resultAsJsonString ?
          JSON.parse(data[0].resultAsJsonString) :
          null;

        if (preference && _.has(preference, ShowInactiveEntitiesKey) &&
          _.isBoolean(preference.showInactiveEntities)) {
          this.setState({ showInactiveEntities: preference.showInactiveEntities });
        }
      }
    });
  }

  setShowInactveEntitesUserPreference = (showInactiveEntities) => {
    const data = JSON.stringify({
      showInactiveEntities,
    });

    this.props.ccCustomerService.setUserPreferences({
      data,
      uid: this.props.userId,
      id: AccountSelectorInactiveAccountsKey,
    });
  }

  toggleShowInactiveEntities = () => {
    const updatedState = !this.state.showInactiveEntities;

    this.setState({ showInactiveEntities: updatedState });
    this.setShowInactveEntitesUserPreference(updatedState);
  }

  convertToBreadCrumbModel = (entities, url) => {
    const list = _.map(entities, entity => ({
      key: entity.id && entity.id.toString(),
      text: entity.name,
      href: this.constructNodeUrl(entity, url),
    }));
    // push selected entity with name and null href
    list.push({ key: 'selected', text: this.props.selectedEntity.name, href: null });
    return list;
  };

  filterNode = node => this.props.ignoreFilter || this.state.showInactiveEntities || node.isActive;

  updateHrefForHierarchyNodes = (nodes, url) =>
    _.each(nodes, (node) => {
      const updatedNode = node;
      updatedNode.href = this.constructNodeUrl(node, url);
      if (updatedNode.children) {
        updatedNode.children = this.updateHrefForHierarchyNodes(updatedNode.children, url);
      }
      return updatedNode;
    });

  constructNodeUrl = (node, url) =>
    this.props.updateEntityUrl({
      isCustomer: node.isCustomer,
      url,
      cid: node.isCustomer ? node.id : node.customerIdInHierarchy,
      aid: node.isCustomer ? null : node.id,
      uid: this.props.userId,
    });

  // iterate over the nodes and update the href if authorized
  toHierarchyModel = (entityNodes) => {
    if (_.isNull(entityNodes)) {
      return { nodes: [], expandNode: false };
    }

    const nodes = entityNodes;
    let expandNode = false;

    _.each(nodes, (node) => {
      const updatedNode = node;

      // IsActive if it the entity is a customer or not in inactive status
      updatedNode.isActive = updatedNode.isCustomer ||
        !_.contains(InActiveAccountLifeCycleStatus, updatedNode.lifeCycleStatus);
      updatedNode.status = updatedNode.isActive ?
        '' :
        this.props.i18n.getString(_TL_('(inactive)'));

      updatedNode.state = updatedNode.state || {};
      updatedNode.isExpandable = node.isCustomer;
      updatedNode.isCurrent = updatedNode.id === this.props.selectedEntity.id;

      // if the node doesnt have children, no need to expand the node
      if (updatedNode.isCustomer && updatedNode.children) {
        const childNodes = this.toHierarchyModel(node.children);
        updatedNode.children = childNodes.nodes;
        // expand the node if in the path of the selected entity
        updatedNode.state.expanded = childNodes.expandNode ||
          (updatedNode.id === this.props.selectedEntity.id);
      }
      expandNode = expandNode || updatedNode.state.expanded || updatedNode.isCurrent;
      return updatedNode;
    });
    return { nodes, expandNode };
  }

  fetchAncestorHierarchyPath = () => {
    // Track performance of initial component on mount call
    if (this.perfMarkerSelect && _.isFunction(this.perfMarkerSelect.willFetchData)) {
      this.perfMarkerSelect.willFetchData();
    }

    this.props.ccCustomerService.getAncestorHierarchyPath({
      cid: this.props.rootCustomerId,
      uid: this.props.userId,
      entityId: this.props.selectedEntity.id,
      isCustomer: this.props.selectedEntity.isCustomer,
    }).then((data) => {
      if (this.perfMarkerSelect && _.isFunction(this.perfMarkerSelect.willRender)) {
        this.perfMarkerSelect.willRender();
      }
      this.setState({
        parentCustomerEntities: data.parentCustomerEntities.reverse(),
      });
    });
  }

  fetchCustomerHierarchy = ({ cid, start, isFromSearch = false }) => {
    this.setState({ loadingHierarchyData: true });
    return this.props.ccCustomerService.getCustomerHierarchy({
      cid,
      uid: this.props.userId,
      start,
      searchTerm: this.enableSearchContext ? this.state.searchValue : '',
      limit: this.config.enableOnScrollLoad ? this.config.batchSize : null,
      returnOnlyDirectlyLinkedEntities: this.config.returnOnlyDirectlyLinkedEntities,
    }).then((data) => {
      const entities = this.toHierarchyModel(data.customerHierarchyEntities);
      const nodes = isFromSearch ? [] : [].concat(this.state.nodes);
      MergeNodesIntoTree(nodes, entities.nodes);
      this.setState({ loadingHierarchyData: false });
      this.onChange(nodes);
    }).catch(() => { this.setState({ loadingHierarchyData: false }); });
  }

  render = () => {
    // Adding a perf marker to track the time it takes for the switch accounts label
    // to finish searching a hierarchy after click
    const switchAccountLabelOnClick = () => {
      this.props.switchAccountsLabelClicked();
    };

    const url = this.props.routeLocation;
    const switchAccountsLabel = (
      <div
        role="button"
        tabIndex="0"
        className="switch-accounts"
        onClick={switchAccountLabelOnClick}
        onKeyDown={submitEvent(this.props.switchAccountsLabelClicked)}
      >
        <span>{this.props.i18n.getString(_TL_('Click to switch accounts'))}</span>
      </div>
    );
    const nodeRenderer = (node, args) => {
      const renderer = this.enableSearchContext ?
        SimpleNodeRenderer :
        CustomerAccountNodeRenderer;
      const newNode = _.extend(node, { nodeAriaLabelledby: allAccountsTitleId });
      return renderer(newNode, this.props.nodeRenderer, args);
    };
    const entitiesWithUpdatedUrls = this.updateHrefForHierarchyNodes(this.state.nodes, url);

    // Show label and tree view both when the parent component
    // overrides the implementation logic for toggling between label and treeview
    const showSwitchAccountsLabel = this.props.overrideToggleEvent ||
      !this.props.showAccountSelectorView;
    const showAccountSelectorView = this.props.overrideToggleEvent ||
      this.props.showAccountSelectorView;

    const InactiveAccountFilter = this.props.inactiveAccountFilter ||
      (this.props.ignoreFilter ? null : Checkbox);

    // Perf marker to track the time it takes for the switchAccountsLabel call to finish
    const perfMarkerDone = () => {
      if (this.perfMarkerSelect &&
        !this.perfMarkerSelect.isDone &&
        _.isFunction(this.perfMarkerSelect.done) &&
        this.perfMarkerSelect.markers.timeRender) {
        this.perfMarkerSelect.done();
      }
      return true;
    };

    // TODO: enable scroll to entity only on first render with non null nodes.
    // Looks like it is getting more complicated with onScrollLoad Enabled.
    // Will handle it in a different PR
    return (
      <UiBlocker
        blocking={this.state.loadingHierarchyData}
        loadingText={this.props.i18n.getString(_TL_('Loading ...'))}
      >
        <Breadcrumbs
          items={this.convertToBreadCrumbModel(this.state.parentCustomerEntities, url)}
          itemRenderer={this.props.breadcrumbItemRenderer}
        />
        {showSwitchAccountsLabel && switchAccountsLabel}
        {perfMarkerDone() && showAccountSelectorView && (
          <div className="account-selector-view">
            {InactiveAccountFilter && (
              <InactiveAccountFilter
                className="toggle-showinactive-accounts"
                checked={this.state.showInactiveEntities}
                onChange={this.toggleShowInactiveEntities}
              >
                {this.props.i18n.getString(_TL_('Show inactive accounts'))}
              </InactiveAccountFilter>
            )}
            <SearchBox
              onSearch={this.onSearch}
              onQueryChange={this.onQueryChange}
              value={this.state.searchValue}
              placeholder={this.props.i18n.getString(_TL_('Search all accounts'))}
            />
            <span
              id={allAccountsTitleId}
              className="title-span"
              role="heading"
              aria-level="2"
            >
              {this.props.i18n.getString(_TL_('All accounts'))}
            </span>
            <CustomerHierarchyTreeView
              nodes={entitiesWithUpdatedUrls}
              onChange={this.onChange}
              filterNode={this.filterNode}
              fetchCustomerHierarchy={this.fetchCustomerHierarchy}
              onMountCallback={this.getShowInactveEntitesUserPreference}
              rootCustomerId={this.props.rootCustomerId}
              nodeRenderer={nodeRenderer}
              i18n={this.props.i18n}
              autoHeight={this.config.autoHeight}
              hierarchyClassNames={CustomerHierarchyClassName}
              enableOnScrollLoad={this.config.enableOnScrollLoad}
              cellHeight={this.props.cellHeight}
              getClassNames={this.getNodeSpecificClassNames}
            />
          </div>
        )}
      </UiBlocker>
    );
  }
}

CustomerAccountSelector.propTypes = AccountSelectorPropTypes;

CustomerAccountSelector.defaultProps = {
  nodeRenderer: DefaultNodeRenderer,
  breadcrumbItemRenderer: undefined,
  updateEntityUrl: DefaultUpdateEntityUrl,
  cellHeight: _.noop(),
  showAccountSelectorView: false,
  overrideToggleEvent: false,
  ignoreFilter: true,
  inactiveAccountFilter: undefined,
  switchAccountsLabelClicked: () => _.noop(),
  batchSize: 1000,
  autoHeight: true,
  returnOnlyDirectlyLinkedEntities: false,
  enableOnScrollLoad: false,
  perfMarker: null,
};
